import type {
  EconomicMetricObservation,
  EconomicProfileSnapshot,
} from '../../types/economicContribution';

const WORKFORCE_ROLE_GROUPS = [
  {
    id: 'facilities-trades',
    label: 'Facilities trades',
    occupationCodes: ['47-2111', '49-9021', '49-9041'],
  },
  {
    id: 'it-support',
    label: 'IT support',
    occupationCodes: ['15-1231', '15-1232'],
  },
  {
    id: 'security',
    label: 'Security',
    occupationCodes: ['33-9032'],
  },
] as const;

const WORKFORCE_METRICS = new Set<EconomicMetricObservation['metric']>([
  'employment',
  'median-hourly-wage',
  'labor-force',
  'unemployment-rate',
]);

const EXPECTED_OCCUPATION_CODES = WORKFORCE_ROLE_GROUPS.flatMap((group) => group.occupationCodes);
const EXPECTED_FACT_COUNT = (EXPECTED_OCCUPATION_CODES.length * 2) + 2;

const OCCUPATION_DISPLAY_LABELS: Readonly<Record<string, string>> = {
  '47-2111': 'Electricians',
  '49-9021': 'HVAC mechanics and installers',
  '49-9041': 'Industrial machinery mechanics',
  '15-1231': 'Network support specialists',
  '15-1232': 'User support specialists',
  '33-9032': 'Security guards',
};

export const WORKFORCE_PROFILE_CAVEATS = [
  'Occupation employment is the number already employed across the published labor-market area. It is not a count of available candidates or a forecast of project hires.',
  'Not every role is equally high-paying. The published security-guard median is shown separately and may be materially below skilled technical roles; data-center security may also be contracted, and no figure is a promised project wage.',
] as const;

export interface WorkforceOccupationFact {
  occupationCode: string;
  occupationLabel: string;
  employment: EconomicMetricObservation | null;
  medianHourlyWage: EconomicMetricObservation | null;
  provenance: readonly string[];
}

export interface WorkforceRoleGroup {
  id: string;
  label: string;
  occupations: readonly WorkforceOccupationFact[];
  /** Null when any occupation is missing employment or employment uses a different published basis. */
  publishedEmploymentTotal: number | null;
  /** Null when any occupation is missing a wage or wage observations use different published bases. */
  publishedHourlyWageRange: { low: number; high: number } | null;
  provenance: string;
  complete: boolean;
}

export interface WorkforceProfilePresentation {
  status: 'available' | 'partial' | 'unavailable';
  statusLabel: 'Available' | 'Partial coverage' | 'Unavailable';
  countyLabel: string;
  roleGroups: readonly WorkforceRoleGroup[];
  laborForce: EconomicMetricObservation | null;
  unemploymentRate: EconomicMetricObservation | null;
  coveredFactCount: number;
  expectedFactCount: number;
  missingWorkforceMetricCount: number;
  workforceProviderIssueCount: number;
  generatedAt: string;
}

function observationBasisKey(observation: EconomicMetricObservation): string {
  return [
    observation.sourceDataset,
    observation.geography.kind,
    observation.geography.code,
    observation.period,
  ].join(':');
}

function selectObservation(
  observations: readonly EconomicMetricObservation[],
  metric: EconomicMetricObservation['metric'],
  occupationCode?: string,
): EconomicMetricObservation | null {
  return observations
    .filter((observation) => (
      observation.metric === metric
      && (occupationCode == null || observation.occupation?.code === occupationCode)
    ))
    .sort((left, right) => (
      left.fallbackLevel - right.fallbackLevel
      || right.period.localeCompare(left.period)
      || left.sourceDataset.localeCompare(right.sourceDataset)
    ))[0] ?? null;
}

function geographyKindLabel(kind: EconomicMetricObservation['geography']['kind']): string {
  switch (kind) {
    case 'metro': return 'metro area';
    case 'nonmetro': return 'nonmetro area';
    case 'county': return 'county';
    case 'state': return 'state';
    case 'national': return 'national';
  }
}

export function formatEconomicPeriod(period: string): string {
  const monthMatch = /^(\d{4})-(\d{2})$/.exec(period);
  if (monthMatch) {
    const year = Number(monthMatch[1]);
    const month = Number(monthMatch[2]);
    if (month >= 1 && month <= 12) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(Date.UTC(year, month - 1, 1)));
    }
  }
  return period;
}

export function workforceObservationProvenance(observation: EconomicMetricObservation): string {
  return `${observation.geography.label} · ${geographyKindLabel(observation.geography.kind)} · ${observation.sourceDataset} · ${formatEconomicPeriod(observation.period)}`;
}

export function formatPublishedEmployment(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

export function formatPublishedHourlyWage(value: number): string {
  return `${new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)}/hour`;
}

export function formatPublishedPercent(value: number): string {
  return `${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)}%`;
}

function primaryObservation(group: WorkforceRoleGroup): EconomicMetricObservation | null {
  for (const occupation of group.occupations) {
    if (occupation.employment) return occupation.employment;
    if (occupation.medianHourlyWage) return occupation.medianHourlyWage;
  }
  return null;
}

function firstCompleteOccupation(group: WorkforceRoleGroup): WorkforceOccupationFact | null {
  return group.occupations.find((occupation) => (
    occupation.employment != null
    && occupation.medianHourlyWage != null
    && observationBasisKey(occupation.employment) === observationBasisKey(occupation.medianHourlyWage)
  )) ?? null;
}

function sentenceProvenance(observation: EconomicMetricObservation): string {
  return `${observation.geography.label} (${observation.sourceDataset}, ${formatEconomicPeriod(observation.period)})`;
}

/**
 * Builds the concise answer shown above the detail table from the same facts
 * and compatibility rules as the workforce presentation. Missing facts are
 * omitted rather than inferred.
 */
export function buildWorkforceProfileAnswer(
  presentation: WorkforceProfilePresentation,
): string {
  const facilities = presentation.roleGroups.find((group) => group.id === 'facilities-trades');
  const itSupport = presentation.roleGroups.find((group) => group.id === 'it-support');
  const parts: string[] = [];

  if (facilities?.publishedEmploymentTotal != null) {
    const source = primaryObservation(facilities);
    const basis = source ? sentenceProvenance(source) : 'the published labor-market area';
    const wageRange = facilities.publishedHourlyWageRange;
    parts.push(
      `${basis} reports ${formatPublishedEmployment(facilities.publishedEmploymentTotal)} people employed across electricians, HVAC mechanics, and industrial machinery mechanics${wageRange ? `, with median hourly wages from ${formatPublishedHourlyWage(wageRange.low)} to ${formatPublishedHourlyWage(wageRange.high)}` : ''}.`,
    );
  } else if (facilities) {
    const occupation = firstCompleteOccupation(facilities);
    if (occupation?.employment && occupation.medianHourlyWage) {
      parts.push(
        `${sentenceProvenance(occupation.employment)} reports ${formatPublishedEmployment(occupation.employment.value)} ${occupation.occupationLabel.toLowerCase()} employed, with a published median wage of ${formatPublishedHourlyWage(occupation.medianHourlyWage.value)}; other facilities-trade observations remain listed individually because the complete set does not share one compatible basis.`,
      );
    }
  }

  if (itSupport?.publishedEmploymentTotal != null) {
    const source = primaryObservation(itSupport);
    const facilitiesSource = facilities ? primaryObservation(facilities) : null;
    const sameArea = source != null && facilitiesSource != null
      && source.geography.kind === facilitiesSource.geography.kind
      && source.geography.code === facilitiesSource.geography.code
      && source.period === facilitiesSource.period;
    parts.push(
      `IT-support occupations account for ${formatPublishedEmployment(itSupport.publishedEmploymentTotal)} employed${sameArea ? ' in the same area' : source ? ` in ${sentenceProvenance(source)}` : ''}.`,
    );
  } else if (itSupport) {
    const occupation = firstCompleteOccupation(itSupport);
    if (occupation?.employment && occupation.medianHourlyWage) {
      parts.push(
        `${sentenceProvenance(occupation.employment)} reports ${formatPublishedEmployment(occupation.employment.value)} ${occupation.occupationLabel.toLowerCase()} employed, with a published median wage of ${formatPublishedHourlyWage(occupation.medianHourlyWage.value)}.`,
      );
    }
  }

  if (presentation.laborForce && presentation.unemploymentRate) {
    const sameLaborBasis = observationBasisKey(presentation.laborForce) === observationBasisKey(presentation.unemploymentRate);
    parts.push(
      `${presentation.laborForce.geography.label} has a labor force of ${formatPublishedEmployment(presentation.laborForce.value)}${sameLaborBasis ? ` and ${formatPublishedPercent(presentation.unemploymentRate.value)} unemployment (${presentation.laborForce.sourceDataset}, ${formatEconomicPeriod(presentation.laborForce.period)})` : `; unemployment is ${formatPublishedPercent(presentation.unemploymentRate.value)} in ${sentenceProvenance(presentation.unemploymentRate)}`}.`,
    );
  } else if (presentation.laborForce) {
    parts.push(`${sentenceProvenance(presentation.laborForce)} reports a labor force of ${formatPublishedEmployment(presentation.laborForce.value)}.`);
  } else if (presentation.unemploymentRate) {
    parts.push(`${sentenceProvenance(presentation.unemploymentRate)} reports ${formatPublishedPercent(presentation.unemploymentRate.value)} unemployment.`);
  }

  if (parts.length === 0) {
    return 'Public workforce context is not available yet. The app loads it automatically after the site resolves to a U.S. county; no manual entry is required.';
  }
  parts.push('These figures show that relevant occupations exist in the surrounding labor market, making local recruitment plausible; they do not measure available candidates or guarantee local hiring.');
  return parts.join(' ');
}

function workforceMissingMetricCount(profile: EconomicProfileSnapshot): number {
  return profile.missingMetrics.filter((entry) => (
    entry.startsWith('labor-force')
    || entry.startsWith('unemployment-rate')
    || entry.startsWith('employment (occupation')
    || entry.startsWith('median-hourly-wage (occupation')
  )).length;
}

function workforceProviderIssueCount(profile: EconomicProfileSnapshot): number {
  return profile.providerErrors.filter((entry) => /^(OEWS|LAUS|BLS)\b/i.test(entry)).length;
}

export function buildWorkforceProfilePresentation(
  profile: EconomicProfileSnapshot,
): WorkforceProfilePresentation {
  const relevantObservations = profile.observations.filter((observation) => (
    WORKFORCE_METRICS.has(observation.metric)
    && (
      observation.metric === 'labor-force'
      || observation.metric === 'unemployment-rate'
      || (observation.occupation != null && EXPECTED_OCCUPATION_CODES.includes(
        observation.occupation.code as (typeof EXPECTED_OCCUPATION_CODES)[number],
      ))
    )
  ));

  const roleGroups = WORKFORCE_ROLE_GROUPS.map((group): WorkforceRoleGroup => {
    const occupations = group.occupationCodes.map((occupationCode): WorkforceOccupationFact => {
      const employment = selectObservation(relevantObservations, 'employment', occupationCode);
      const medianHourlyWage = selectObservation(relevantObservations, 'median-hourly-wage', occupationCode);
      const fallbackLabel = OCCUPATION_DISPLAY_LABELS[occupationCode]
        ?? employment?.occupation?.label
        ?? medianHourlyWage?.occupation?.label
        ?? occupationCode;
      return {
        occupationCode,
        occupationLabel: fallbackLabel,
        employment,
        medianHourlyWage,
        provenance: [...new Set(
          [employment, medianHourlyWage]
            .filter((observation): observation is EconomicMetricObservation => observation != null)
            .map(workforceObservationProvenance),
        )],
      };
    });
    const observations = occupations.flatMap((occupation) => (
      [occupation.employment, occupation.medianHourlyWage]
        .filter((observation): observation is EconomicMetricObservation => observation != null)
    ));
    const employmentObservations = occupations
      .map((occupation) => occupation.employment)
      .filter((observation): observation is EconomicMetricObservation => observation != null);
    const wageObservations = occupations
      .map((occupation) => occupation.medianHourlyWage)
      .filter((observation): observation is EconomicMetricObservation => observation != null);
    const bases = new Set(observations.map(observationBasisKey));
    const employmentBases = new Set(employmentObservations.map(observationBasisKey));
    const wageBases = new Set(wageObservations.map(observationBasisKey));
    const allEmploymentPublished = occupations.every((occupation) => occupation.employment != null);
    const allWagesPublished = occupations.every((occupation) => occupation.medianHourlyWage != null);
    const complete = occupations.every((occupation) => (
      occupation.employment != null && occupation.medianHourlyWage != null
    ));
    const consistentBasis = bases.size === 1;
    const wageValues = wageObservations.map((observation) => observation.value);
    return {
      id: group.id,
      label: group.label,
      occupations,
      publishedEmploymentTotal: allEmploymentPublished && employmentBases.size === 1
        ? occupations.reduce((sum, occupation) => sum + (occupation.employment?.value ?? 0), 0)
        : null,
      publishedHourlyWageRange: allWagesPublished && wageBases.size === 1
        ? { low: Math.min(...wageValues), high: Math.max(...wageValues) }
        : null,
      provenance: observations.length === 0
        ? 'No published observation'
        : consistentBasis
          ? workforceObservationProvenance(observations[0]!)
          : 'Mixed published geographies or vintages · shown by occupation',
      complete,
    };
  });

  const laborForce = selectObservation(relevantObservations, 'labor-force');
  const unemploymentRate = selectObservation(relevantObservations, 'unemployment-rate');
  const coveredFactCount = roleGroups.reduce((count, group) => (
    count + group.occupations.reduce((groupCount, occupation) => (
      groupCount + Number(occupation.employment != null) + Number(occupation.medianHourlyWage != null)
    ), 0)
  ), Number(laborForce != null) + Number(unemploymentRate != null));
  const facilitiesTrades = roleGroups.find((group) => group.id === 'facilities-trades');
  const itSupport = roleGroups.find((group) => group.id === 'it-support');
  const completeOccupationCount = (group: WorkforceRoleGroup | undefined): number => (
    group?.occupations.filter((occupation) => (
      occupation.employment != null
      && occupation.medianHourlyWage != null
      && observationBasisKey(occupation.employment) === observationBasisKey(occupation.medianHourlyWage)
    )).length ?? 0
  );
  // Security and every individual occupation cell are useful context, but are
  // not all prerequisites for the screening question. A defensible local
  // workforce answer needs multiple facilities-trade observations, at least
  // one IT-support observation, and a county labor-market indicator. Missing
  // optional cells remain visible in the coverage note and are never imputed.
  const decisionReady = completeOccupationCount(facilitiesTrades) >= 2
    && completeOccupationCount(itSupport) >= 1
    && (laborForce != null || unemploymentRate != null);
  const status = decisionReady
    ? 'available'
    : coveredFactCount > 0
      ? 'partial'
      : 'unavailable';

  return {
    status,
    statusLabel: status === 'available' ? 'Available' : status === 'partial' ? 'Partial coverage' : 'Unavailable',
    countyLabel: `${profile.countyName}, ${profile.stateCode}`,
    roleGroups,
    laborForce,
    unemploymentRate,
    coveredFactCount,
    expectedFactCount: EXPECTED_FACT_COUNT,
    missingWorkforceMetricCount: workforceMissingMetricCount(profile),
    workforceProviderIssueCount: workforceProviderIssueCount(profile),
    generatedAt: profile.generatedAt,
  };
}
